import * as React from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import PharmacyDetails from "./PharmacyDetails/PharmacyDetails";
import PhysicalAddress from "./PhysicalAddress/PhysicalAddress";
import OtherDetails from "./OtherDetails/OtherDetails";
import ReviewFollowingStatements from "./ReviewFollowingStatements/ReviewFollowingStatements";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useMutationCreatePharmacy } from "../../../queries/pharmacy/hooks/useMutationCreatePharmacy";
import { NotificationType, SERVER_URL } from "../../../constants/global";
import { useContext, useState } from "react";
import { ToasterContext } from "../../shared/Toaster/ToasterContext";
import { Alert, AlertTitle, CircularProgress, Typography } from "@mui/material";
import { useQueryPharmacyOptions } from "../../../queries/pharmacy/hooks/useQueryPharmacyOptions";
import { IUser } from "../../../queries/user/userModel";
import {
  pharmacyDetailsValidationSchema,
  physicalAddressValidationSchema,
  otherDetailsValidationSchema,
  bankAccountValidationSchema
} from "../../../constants/validationSchemas";
import { useAuth } from "../../../hooks/useAuth";
import BusinessInformation from "./BusinessInformation/BusinessInformation";
import { useQueryFindPharmacy } from "../../../queries/pharmacy/hooks/useQueryFindPharmacy";
import { validateStatements } from "../../../constants/pharmacy";
import { additionalQuestions } from "../../../constants/signupQuestions";
import AdditionalQuestions from './AdditionalQuestions/AdditionalQuestions';

const steps = [
  'Pharmacy Details',
  'Physical Address',
  'Bank Information',
  'Other Details',
  'Review the following statements',
  'Additional Questions',
];

// Sanitize filenames to avoid issues with special characters (e.g., '#') during upload
const sanitizeFilename = (name: string) => {
  // Keep alphanumerics, dot, dash, underscore; replace others with '_'
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

interface IPharmacyProps {
  user: IUser
}
const NewPharmacy: React.FunctionComponent<IPharmacyProps> = ({
  user
}) => {
  const { token, signOut, checkSession } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useContext(ToasterContext);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [deaLicenseFile, setDeaLicenseFile] = useState<any>(null);
  const [ncpdpLicenseFile, setNcpdpLicenseFile] = useState<any>(null);
  const [businessDocumentFile, setBusinessDocumentFile] = useState<any>(null);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [requiredItems, setRequiredItems] = useState<any>([]);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  const { data: pharmacyOptionData, loading: pharmacyOptionsLoading } = useQueryPharmacyOptions({});
  const [createPharmacy, { loading }] = useMutationCreatePharmacy();
  const [findPharmacy] = useQueryFindPharmacy({
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache'
  });

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getValidationScheme = () => {
    if (activeStep === 0) {
      return pharmacyDetailsValidationSchema(findPharmacy)
    } else if (activeStep === 1) {
      return physicalAddressValidationSchema
    } else
      if (activeStep === 2) {
        return bankAccountValidationSchema;
      }
      else if (activeStep === 3) {
        return otherDetailsValidationSchema;
      } else {
        return Yup.object().shape({});
      }
  }

  const { register, handleSubmit, formState: { errors }, control } = useForm({
    resolver: yupResolver(getValidationScheme()),
    mode: "onChange",
  });

  const hasRequiredStatements = (statements: any) => {
    let hasRequired = false;
    Object.keys(statements).forEach((statement: any) => {
      if (statements[statement] === undefined) {
        hasRequired = true;
        return false;
      }
    });

    return hasRequired;
  }

  const handleNext = async (values: any) => {
    // Check session validity on every step transition
    const isSessionValid = await checkSession();
    if (!isSessionValid) {
      setSessionExpired(true);
      return;
    }

    if (!deaLicenseFile || !ncpdpLicenseFile) {
      addToast({ type: NotificationType.Error, title: 'DEA and Pharmacy license files are required' });
    } else if (!businessDocumentFile) {
      addToast({ type: NotificationType.Error, title: '147c document required' });
    } else {
      if (activeStep === 5) {
        const invalidData = additionalQuestions.findIndex((item: any) => {
          return (!values[item.name] || (item.options.find((option: any) => option.key === values[item.name]).additionalField && !values[`${item.name}-additionalAnswer`]))
        })
        if (invalidData !== -1) {
          setFilesLoading(false);
          return addToast({ type: NotificationType.Error, title: 'All fields are required' });
        }
      }
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      setActiveStep((prevActiveStep) => prevActiveStep === steps.length - 1 ? prevActiveStep : prevActiveStep + 1);
      setSkipped(newSkipped);

      if (activeStep === steps.length - 1) {
        setFilesLoading(true);
        const newObject = { ...values }
        const statements: any = Object.keys(newObject).filter((key) => key.includes("statement-")).reduce((obj, key) => {
          return Object.assign(obj, {
            [key]: newObject[key]
          });
        }, {});

        setRequiredItems(statements);

        const statementData = Object.keys(statements).map((statement: string) => {
          const statementIds: string[] = statement.split('-');
          return {
            answer: statements[statement] === 'YES',
            statementId: +statementIds[1]
          }
        });

        if ((!statements['statement-13'] || statements['statement-13'] === 'NO') && (!statements['statement-14'] || statements['statement-14'] === 'NO')) {
          setFilesLoading(false);
          addToast({ type: NotificationType.Error, title: 'If you do not agree to follow all federal and state regulations including Drug Supply Chain Security Act (DSCSA), unfortunately, we cannot allow you to utilize our services.' });
          addToast({ type: NotificationType.Error, title: ' If you do not agree to the Privacy Policy or User Agreement, unfortunately, we cannot allow you to access our website or utilize our services.' });
        } else if (!statements['statement-13'] || statements['statement-13'] === 'NO') {
          setFilesLoading(false);
          addToast({ type: NotificationType.Error, title: 'If you do not agree to follow all federal and state regulations including Drug Supply Chain Security Act (DSCSA), unfortunately, we cannot allow you to utilize our services.' });
        } else if (!statements['statement-14'] || statements['statement-14'] === 'NO') {
          setFilesLoading(false);
          addToast({ type: NotificationType.Error, title: ' If you do not agree to the Privacy Policy or User Agreement, unfortunately, we cannot allow you to access our website or utilize our services.' });
        } else if (hasRequiredStatements(statements)) {
          setFilesLoading(false);
          addToast({ type: NotificationType.Error, title: 'All the questions are required. Please ensure that you have answered all of them before submitting the form. Your input is essential for verification purposes.' });
        } else if (!validateStatements(statements)) {
          setFilesLoading(false);
          addToast({ type: NotificationType.Error, title: ' We regret to inform you that your pharmacy doesn\'t meet the requirements to buy or sell on our platform.' });
        } else {
          let requestData: any = {
            deaNumber: values.deaNumber,
            email: values.email,
            fax: values.fax,
            federalTaxId: values.federalTaxId,
            legalName: values.legalName,
            mobile: values.mobile,
            ncpdp: values.ncpdp,
            npi: values.npi,
            phone: values.phone,
            site: values.site,
            softwareName: values.softwareName,
            store: +values.store,
            AdditionalQuestions: {
              create: additionalQuestions.map((item: any) => (
                {
                  question: item.question,
                  answer: values[item.name],
                  additionalAnswer: item.options.find((option: any) => option.key === values[item.name]).additionalField ? values[`${item.name}-additionalAnswer`] : '',
                }
              ))
            },
            PharmacyDetail: {
              create: {
                contractManager: values.contractManager,
                managerContactName: values.managerContactName,
                is340B: values.is340B === 'YES',
                PharmacyDetailTypes: {
                  create: {
                    PharmacyType: {
                      connect: {
                        id: values.pharmacyType
                      }
                    }
                  }
                }
              }
            },
            PharmacyLicensedStates: {
              create: {
                State: {
                  connect: {
                    id: values.stateLicense
                  }
                }
              }
            },
            PharmacyAddress: {
              create: {
                AddressType: {
                  connect: {
                    id: pharmacyOptionData.findFirstAddressType.id
                  }
                },
                State: {
                  connect: {
                    id: values.state
                  }
                },
                address: values.physicalAddress,
                city: values.city,
                zipCode: values.zipCode
              }
            },
            PharmacyStatement: {
              createMany: {
                data: statementData,
                skipDuplicates: false
              }
            },
            PharmacyUsers: {
              create: {
                User: {
                  connect: {
                    id: +user.id
                  }
                }
              }
            },
            PharmacyStatus: {
              create: {
                Status: {
                  connect: {
                    id: pharmacyOptionData.findManyStatus.find((status: any) => status.key === 'PENDING').id
                  }
                }
              }
            },
            PharmacyFiles: {
              createMany: {
                data: []
              }
            },
            BankAccount: {
              create: {
                accountHolderName: values.accountHolderName,
                routingNumber: values.routingNumber,
                object: 'bank_account',
                User: {
                  connect: {
                    id: +user.id
                  }
                }
              }
            },
            PharmacyBusinessType: {
              connect: {
                id: 6
              }
            },
          }

          const deaLicenseFileData = new FormData();
          const newDeaLicenseFile = new File([deaLicenseFile], `${Date.now()}_${sanitizeFilename(deaLicenseFile.name)}`, {
            type: deaLicenseFile.type,
          });
          deaLicenseFileData.append('file', newDeaLicenseFile);
          deaLicenseFileData.append('fileType', 'deaLicense');
          const deaLicenseFileUpload = fetch(`${SERVER_URL}/file/upload`, {
            method: 'POST',
            body: deaLicenseFileData,
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const ncpdpLicenseFileData = new FormData();
          const newNcpdpLicenseFile = new File([ncpdpLicenseFile], `${Date.now()}_${sanitizeFilename(ncpdpLicenseFile.name)}`, {
            type: ncpdpLicenseFile.type,
          });
          ncpdpLicenseFileData.append('file', newNcpdpLicenseFile);
          ncpdpLicenseFileData.append('fileType', 'ncpdpLicense');
          const ncpdpLicenseFileUpload = fetch(`${SERVER_URL}/file/upload`, {
            method: 'POST',
            body: ncpdpLicenseFileData,
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const businessFileData = new FormData();
          const newBusinessDocumentFile = new File([businessDocumentFile], `${Date.now()}_${sanitizeFilename(businessDocumentFile.name)}`, {
            type: businessDocumentFile.type,
          });
          businessFileData.append('file', newBusinessDocumentFile);
          businessFileData.append('fileType', 'irs147CLetter');
          const businessFileUpload: any = await fetch(`${SERVER_URL}/file/upload`, {
            method: 'POST',
            body: businessFileData,
            headers: { 'Authorization': `Bearer ${token}` }
          });

          try {
            const [data1, data2, data3]: any = await Promise.all([deaLicenseFileUpload, ncpdpLicenseFileUpload, businessFileUpload]);

            // Check if any upload returned 401 (session expired)
            const responses = [data1, data2, data3];
            const unauthorizedResponse = responses.find((r: any) => r.status === 401 || r.status === 403);
            if (unauthorizedResponse) {
              setFilesLoading(false);
              setSessionExpired(true);
              return;
            }

            if (data1.ok && data2.ok && data3.ok) {
              const data1Res = await data1.json();
              const data2Res = await data2.json();
              const data3Res = await data3.json();

              requestData.PharmacyFiles.createMany.data = [
                ...requestData.PharmacyFiles.createMany.data,
                { fileId: data1Res.id },
                { fileId: data2Res.id },
                { fileId: data3Res.id },
              ];

              createPharmacy({ variables: { data: requestData, accountNumber: values.accountNumber } }).then(() => {
                setFilesLoading(false);
                window.location.reload();
                navigate('/');
              }).catch((error: any) => {
                setFilesLoading(false);
                // Check if mutation error is auth-related
                if (error.message?.includes('Unauthorized') || error.message?.includes('UNAUTHENTICATED') || error.message?.includes('jwt expired')) {
                  setSessionExpired(true);
                } else {
                  addToast({ type: NotificationType.Error, title: error.message });
                }
              });
            } else {
              setFilesLoading(false);
              addToast({ type: NotificationType.Error, title: 'File upload failed. Please try again.' });
            }
          } catch (error: any) {
            setFilesLoading(false);
            addToast({ type: NotificationType.Error, title: error.message });
          }
        }
      }
    }
  };

  if (pharmacyOptionsLoading) {
    return <CircularProgress sx={{ position: 'absolute', left: '50%', top: '50%' }} color="inherit" />;
  }

  const handleSessionLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <Box sx={{ width: '70%', mx: 'auto', pt: 4 }} >
      {sessionExpired && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Session Expired</AlertTitle>
          Your session has expired. Your information is still visible below so you can review or copy it before logging in again.
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" size="small" onClick={handleSessionLogout}>
              Log In Again
            </Button>
          </Box>
        </Alert>
      )}
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box component="form" onSubmit={handleSubmit(handleNext)} noValidate sx={{ p: 4, mt: 8, backgroundColor: '#FFFFFF' }}>
        {activeStep === 0 && (
          <PharmacyDetails
            register={register}
            errors={errors}
            control={control}
            states={pharmacyOptionData.findManyState}
            setDeaLicenseFile={setDeaLicenseFile}
            deaLicenseFile={deaLicenseFile}
            setNcpdpLicenseFile={setNcpdpLicenseFile}
            ncpdpLicenseFile={ncpdpLicenseFile}
            businessDocumentFile={businessDocumentFile}
            setBusinessDocumentFile={setBusinessDocumentFile}
          />
        )}
        {activeStep === 1 && (
          <PhysicalAddress
            register={register}
            errors={errors}
            control={control}
            states={pharmacyOptionData.findManyState}
          />
        )}
        {activeStep === 2 && (
          <BusinessInformation
            register={register}
            errors={errors}
          />
        )}
        {activeStep === 3 && (
          <OtherDetails
            register={register}
            errors={errors}
            control={control}
            pharmacyTypes={pharmacyOptionData.findManyPharmacyType}
          />
        )}
        {activeStep === 4 && (
          <ReviewFollowingStatements
            control={control}
            statements={pharmacyOptionData.findManyStatement}
            requiredItems={requiredItems}
          />
        )}
        {activeStep === 5 && (
          <AdditionalQuestions
            register={register}
            errors={errors}
            control={control}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant='outlined'
              disabled={activeStep === 0 || (activeStep === steps.length - 1 && (loading || filesLoading))}
              onClick={handleBack}
              sx={{ mr: 4, width: '200px' }}
              size='large'
            >
              Back
            </Button>
            <Button size='large' type="submit" variant='contained' sx={{ width: '200px' }} disabled={sessionExpired || loading || filesLoading}>
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
              {activeStep === steps.length - 1 && (loading || filesLoading) && <CircularProgress sx={{ ml: 2 }} size={14} color='inherit' />}
            </Button>
          </Box>
          {(loading || filesLoading) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Please do not close this window. Your documents are being uploaded and your registration is being processed.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default NewPharmacy;